import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { of, from } from 'rxjs';
import { LocationService } from '../../../core/services/location.service';
import { LocationActions } from './location.actions';
import { Router } from '@angular/router';
import { IApiResponse, ILocation } from '../../models/location.model';
import { Store } from '@ngrx/store';
import { getQueries } from './location.selectors';
import { AlertService } from 'src/app/core/services/alert.service';

@Injectable()
export class LocationEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly locationService: LocationService,
    private readonly router: Router,
    private readonly store: Store, // Adjust the type according to your state structure
    private readonly alertService: AlertService
  ) {}

  loadLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.loadLocations),
      withLatestFrom(this.store.select(getQueries)),
      mergeMap(([, filters]) => {
        return from(
          this.locationService.getLocations(
            filters.params.page,
            filters.params.limit,
            filters.body ?? {}
          )
        ).pipe(
          map((clients) => {
            return LocationActions.loadLocationsSuccess({
              response: clients,
              filter: filters.body?.filter ?? {},
            });
          }),
          catchError((error) =>
            of(LocationActions.loadLocationsFailure({ error: error.message }))
          )
        );
      })
    )
  );

  createLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.createLocation),
      mergeMap(
        (
          action // Change to receive the action object
        ) =>
          this.locationService.createLocation(action.location).pipe(
            // Use action.location
            map((response) => {
              // Access the closeModalSignal from the action object
              action.closeModalSignal.set(true);
              this.alertService.success(
                response?.message,
                'Success',
                response.data.location_name
              );
              return LocationActions.createLocationSuccess({ response });
            }),
            catchError((error) => {
              return of(
                LocationActions.createLocationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  loadSingleLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.loadSingleLocation),
      mergeMap(({ id }) =>
        this.locationService.getLocation(id).pipe(
          map((response) =>
            LocationActions.loadSingleLocationSuccess({ response })
          ),
          catchError((error) =>
            of(
              LocationActions.loadSingleLocationFailure({
                error: error.message,
              })
            )
          )
        )
      )
    )
  );
  updateLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.updateLocation),
      mergeMap(
        (
          action // Use mergeMap as it's typically suited for update operations
        ) =>
          from(
            this.locationService.updateLocation(action.id, action.location)
          ).pipe(
            map((response: IApiResponse<ILocation>) => {
              action.closeModalSignal.set(true);
              this.alertService.success(
                response?.message,
                'Success',
                response.data.location_name
              );
              return LocationActions.updateLocationSuccess({
                response: response,
              });
            }),
            catchError((error) => {
              return of(
                LocationActions.updateLocationFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  deleteLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.deleteLocation),
      mergeMap((action) =>
        this.locationService.deleteLocation(action.id).pipe(
          map((response) => {
            action.closeModalSignal.set(true); // Trigger the modal close
            this.alertService.success(
              response?.message,
              'Success',
              response.data.location_name
            );
            return LocationActions.deleteLocationSuccess({
              id: action.id,
              message: response.message,
            });
          }),
          catchError((error) => {
            return of(
              LocationActions.deleteLocationFailure({ error: error.message })
            );
          })
        )
      )
    )
  );

  loadLocationNames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.loadLocationNames),
      mergeMap(() =>
        this.locationService.listLocationName().pipe(
          map((response) => {
            return LocationActions.loadLocationNamesSuccess({
              response: response,
            });
          }),
          catchError((error) =>
            of(
              LocationActions.loadLocationNamesFailure({ error: error.message })
            )
          )
        )
      )
    )
  );

  loadAllStatusLocationNames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.loadAllStatusLocationNames),
      mergeMap(() =>
        this.locationService.listAllStatusLocationNames().pipe(
          map((response) => {
            return LocationActions.loadAllStatusLocationNamesSuccess({
              response: response,
            });
          }),
          catchError((error) =>
            of(
              LocationActions.loadAllStatusLocationNamesFailure({
                error: error.message,
              })
            )
          )
        )
      )
    )
  );

  redirectAfterCreate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LocationActions.createLocationSuccess),
        tap(() => {
          this.router.navigate(['/location']);
        })
      ),
    { dispatch: false }
  );

  refreshDataOnChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        LocationActions.createLocationSuccess,
        LocationActions.updateLocationSuccess,
        LocationActions.deleteLocationSuccess,
        LocationActions.changeLocationStatusSuccess
      ),
      // Remove unused withLatestFrom if state isn't needed
      mergeMap(() => [
        LocationActions.loadLocations(), // Original action
        LocationActions.loadAllStatusLocationNames(), // New action to fetch location names
      ])
    )
  );
  changeStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.changeLocationStatus),
      mergeMap((action) =>
        from(this.locationService.toggleStatus(action.id)).pipe(
          map((response: IApiResponse<ILocation>) => {
            this.alertService.success(response.message, 'Success', response.data.location_name);
            return LocationActions.changeLocationStatusSuccess();
          }),
          catchError((error) =>
            of(
              LocationActions.changeLocationStatusFailure({
                error: error.message,
              })
            )
          )
        )
      )
    )
  );
}
